import { FC, useEffect, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import { enumTranslate, ETransactionStatusTranslate } from '@locales/index';
import { Icon } from '@components/icons';
import Status from '@components/common/status';
import {
    copyStringToClipboard,
    formatDate,
    getFirstLettersBySpace,
    getScheduleDescription,
    getScheduleStatusVariant,
    getScheduleTypeByMethodAndAction,
    getStartDate,
    getTransactionStatus,
    prettifyAmount
} from '@utils/index';
import Tabs from '@components/tabs';
import {
    EScheduleStatus,
    ETransactionAction,
    ETransactionMethod,
    ETransactionStatus,
    TTransaction
} from '@xeppt/xeppt-sdk/types';
import { IModalComponentProps } from '@context/modal_context';
import { TSchedule } from '@xeppt/xeppt-sdk/types/schedule';
import type { TScheduleWithReferenceCode } from 'src/types/reference_code';
import {
    getBillingStatusList,
    getFrequencyList,
    getLastPaymentDate,
    getNextPaymentDate
} from '@utils/billing';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames/bind';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiAccountService, apiScheduleService, apiTransactionsService } from '@api';
import { useLocales } from '@hooks/helpers/useLocales';
import { useApiQuery } from '@hooks/api/useApiQuery';
import Typography from '@components/common/typography';
import { currencies } from '@const/currency';
import { ETransactionType, TTransactionLite } from '@xeppt/xeppt-sdk/types/transaction';
import useModalContext from '@hooks/context/useModalContext';
import InfoBlock from '@components/common/info_block';
import Button from '@components/common/button';
import { EFundsOperation } from '@enum';
import ViewTransactionModal from '../view_transaction';

const cx = classNames.bind(styles);

type TScheduleTab = 'schedule' | 'details' | 'transactions' | 'requests';

const ViewScheduleModal: FC<
    IModalComponentProps<{
        schedule: TScheduleWithReferenceCode;
        refetchSchedules?: () => void;
        onEdit: (schedule: TSchedule) => void;
    }>
> = ({ onClose, modalData, ...restProps }) => {
    const { t: frequencyLocale, i18n } = useTranslation('sections', {
        keyPrefix: 'send_and_request.schedule_payments.payment_details'
    });
    const { requestErrorLocale, requestSuccessLocale, submitLocale } = useLocales();
    const [activeTab, setActiveTab] = useState<TScheduleTab>('schedule');
    const [chosenTransaction, setChosenTransaction] = useState('');
    const [chosenTransactionData, setChosenTransactionData] = useState<TTransaction | null>(null);
    const [showFees, setShowFees] = useState(false);
    const { isOpen } = useModalContext();

    const { data: transactions } = useApiQuery({
        method: () =>
            apiTransactionsService.findTransactions({ schedule_id: modalData?.schedule?.id }),
        deps: [modalData?.schedule?.id],
        condition: !!modalData?.schedule?.id
    });

    const { handleRequest: fetchTransaction } = useApiQuery({
        method: () => apiTransactionsService.getTransactionById(chosenTransaction || ''),
        isInitialRequest: false,
        condition: !!chosenTransaction,
        onSuccess: (data) => {
            setChosenTransactionData(data);
            setChosenTransaction('');
        },
        onError: () => {
            setChosenTransaction('');
        }
    });

    const { handleRequest: handleRetrySchedule, isLoading: isRetryLoading } = useApiMutation({
        method: (id: string) => {
            return apiScheduleService.retrySchedule(id);
        },
        onSuccess: () => {
            requestSuccessLocale('retry_schedule');
            fetchTransaction();
            onClose();
        },
        onError: requestErrorLocale
    });

    useEffect(() => {
        if (chosenTransaction) {
            fetchTransaction();
        }
    }, [chosenTransaction]);

    useEffect(() => {
        if (!isOpen) {
            setChosenTransaction('');
            setChosenTransactionData(null);
        }
    }, [isOpen]);

    if (!modalData?.schedule) return null;

    const { schedule, refetchSchedules, onEdit } = modalData;

    const isRequest = useMemo(() => {
        return schedule.action === ETransactionAction.REQUEST;
    }, [schedule]);

    const getRecipientName = useMemo(() => {
        return schedule.info.split('(')?.[0] || '';
    }, [schedule]);

    const getEmail = useMemo(() => {
        return schedule.info.split('(')?.[1]?.replace(')', '') || '';
    }, [schedule]);

    const getAvatar = useMemo(() => {
        if (
            schedule.method === ETransactionMethod.WALLET ||
            schedule.method === ETransactionMethod.E_TRANSFER
        ) {
            return getFirstLettersBySpace(getRecipientName);
        } else if (ETransactionMethod.EFT === schedule.method) {
            return <Icon name="bank" width={24} height={24} />;
        } else if (ETransactionMethod.EPS === schedule.method) {
            return <Icon name="credit_card" width={24} height={24} />;
        } else if (ETransactionMethod.PAY_BILL === schedule.method) {
            return <Icon name="pay_bill" width={24} height={24} />;
        }
        return '';
    }, [schedule, getRecipientName]);

    const getNextPaymentText = useMemo(() => {
        if (schedule.status === EScheduleStatus.COMPLETE) {
            return 'Closed';
        } else if (
            schedule.status === EScheduleStatus.ACTIVE ||
            schedule.status === EScheduleStatus.ERROR
        ) {
            return schedule.action === ETransactionAction.REQUEST ? 'Next request' : 'Next payment';
        } else if (schedule.status === EScheduleStatus.INACTIVE) {
            return 'Paused';
        }
    }, [schedule.action, schedule.status]);

    const { handleRequest: handleCancelPayment } = useApiMutation({
        method: (id: string) => {
            return apiScheduleService.deleteSchedule(id);
        },
        onSuccess: () => {
            requestSuccessLocale('cancel_schedule');
            refetchSchedules && refetchSchedules();
            onClose();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleDeactivatePayment } = useApiMutation({
        method: (id: string) => {
            return apiScheduleService.deactivateSchedule(id);
        },
        onSuccess: () => {
            requestSuccessLocale('pause_schedule');
            refetchSchedules && refetchSchedules();
            onClose();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleActivatePayment } = useApiMutation({
        method: (id: string) => {
            return apiScheduleService.activateSchedule(id);
        },
        onSuccess: () => {
            requestSuccessLocale('activate_schedule');
            refetchSchedules && refetchSchedules();
            onClose();
        },
        onError: requestErrorLocale
    });


    const getTitle = useMemo(() => {
        const type = getScheduleTypeByMethodAndAction(schedule.method, schedule.action);
        const operation = `${schedule.method}_${type}_${schedule.action}`;
        switch (operation) {
            case EFundsOperation.WALLET_CREDIT_TRANSFER:
                return 'Scheduled send money via XEPPT';
            case EFundsOperation.WALLET_DEBIT_REQUEST:
                return 'Scheduled request money via XEPPT';
            case EFundsOperation.EFT_CREDIT_WITHDRAWAL:
                return 'Scheduled withdraw to bank account';
            case EFundsOperation.EFT_DEBIT_DEPOSIT:
                return 'Scheduled load balance';
            case EFundsOperation.PAY_BILL_CREDIT_PAYMENT:
                return 'Scheduled pay bill';
        }
    }, [schedule]);

    const transactionAmount = (transaction: TTransactionLite) => {
        if (transaction.type === ETransactionType.CREDIT) {
            return transaction.amount + transaction.fee;
        }
        return transaction.amount;
    };

    const amountStyles = (transaction: TTransactionLite) =>
        cx([
            styles.amount,
            {
                plus: transaction.type !== ETransactionType.CREDIT,
                isCancel:
                    transaction.status === ETransactionStatus.CANCELED ||
                    transaction.status === ETransactionStatus.DECLINED
            }
        ]);

    const calculateTotalPaid = useMemo(() => {
        return (
            transactions?.data?.reduce((acc, transaction) => {
                if (transaction.status === ETransactionStatus.APPROVED)
                    return acc + transactionAmount(transaction);
                return acc;
            }, 0) || 0
        );
    }, [transactions]);

    const { data: pointsFeeData } = useApiQuery({
        method: () => {
            return apiAccountService.calculateLoyaltyFee({
                fee_included: false,
                amount: schedule.amount,
                method: schedule.method,
                action: schedule.action,
                type: getScheduleTypeByMethodAndAction(schedule.method, schedule.action)
            });
        },
        isInitialRequest: true,
        deps: [schedule],
        condition: !!schedule
    });

    const calculateRemainingAmount = useMemo(() => {
        const fee = pointsFeeData?.fee || 0;
        return (schedule.amount + fee) * schedule.remaining_payments || 0;
    }, [pointsFeeData, schedule]);

    const tabList = useMemo(() => {
        const base = [
            { label: 'Schedule', value: 'schedule' },
            { label: 'Details', value: 'details' }
        ];
        if (isRequest) {
            return [...base, { label: 'Requests', value: 'requests' }];
        }
        return [...base, { label: 'Transactions', value: 'transactions' }];
    }, [isRequest]);

    const showActionBar = [
        EScheduleStatus.ACTIVE,
        EScheduleStatus.INACTIVE,
        EScheduleStatus.ERROR
    ].includes(schedule.status);

    if (chosenTransactionData !== null) {
        return (
            <ViewTransactionModal
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                {...(restProps as any)}
                modalData={{
                    transaction: chosenTransactionData,
                    onBack: () => setChosenTransactionData(null)
                }}
                onClose={onClose}
            />
        );
    }

    if (showFees) {
        const feesCurrency = transactions?.data?.[0]?.currency ?? 'CAD';
        const feesSymbol = currencies[feesCurrency as 'CAD']?.symbol ?? '$';
        const feeStr = `${feesSymbol}${prettifyAmount((pointsFeeData?.fee ?? 0).toFixed(2))} ${feesCurrency}`;
        return (
            <div className={styles.wrapper}>
                <div className={styles.sched_detail_header}>
                    <button
                        type="button"
                        className={styles.sched_detail_back_btn}
                        onClick={() => setShowFees(false)}>
                        <Icon name="arrow_left" width={24} height={24} />
                    </button>
                    <Typography fz={24} fw={700} alignment="center">
                        Total fees
                    </Typography>
                    <button
                        type="button"
                        className={styles.sched_detail_close_btn}
                        onClick={onClose}>
                        <Icon name="close" width={20} height={20} />
                    </button>
                </div>
                <div className={styles.fees_rows}>
                    <div className={styles.fees_item}>
                        <p>XEPPT fee</p>
                        <p className={styles.fees_value}>{feeStr}</p>
                    </div>
                    <hr className={styles.fees_divider} />
                    <div className={styles.fees_item}>
                        <p className={styles.fees_total}>Total</p>
                        <p className={styles.fees_total}>{feeStr}</p>
                    </div>
                    <p className={styles.fees_subtext}>Included into amount you send</p>
                </div>
                <InfoBlock variant="gray">
                    The XEPPT fee is included in the amount you send each cycle.
                </InfoBlock>
            </div>
        );
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.sched_detail_header}>
                <Typography fz={24} fw={700} alignment="center">
                    Schedule details
                </Typography>
                <button
                    type="button"
                    className={styles.sched_detail_close_btn}
                    onClick={onClose}>
                    <Icon name="close" width={20} height={20} />
                </button>
            </div>
            {schedule.status === EScheduleStatus.ERROR && (
                <InfoBlock variant="warning">
                    <div className={styles.error_info}>
                        <Typography variant="body4" weight="semibold">
                            Last payment attempt failed
                        </Typography>
                        <Typography variant="body4">
                            Check your balance and recipient details to ensure they’re correct. Once
                            the issue is resolved, retry the payment.
                        </Typography>
                        <Button
                            size="small"
                            variant="primary"
                            isLoading={isRetryLoading}
                            onClick={() => handleRetrySchedule(schedule.id)}>
                            Retry payment
                        </Button>
                    </div>
                </InfoBlock>
            )}
            <div className={styles.content}>
                <div className={styles.user}>
                    <div className={styles.user_avatar}>{getAvatar}</div>
                    <p className={styles.user_name}>{getRecipientName}</p>
                    <p className={styles.user_email}>{getEmail}</p>
                    <p className={styles.method}>
                        {schedule?.method &&
                            enumTranslate[schedule.method][i18n.language as 'en' | 'fr']}
                    </p>
                    <div className={styles.divider}>
                        <Typography variant="body4">
                            <Icon
                                name={
                                    getScheduleTypeByMethodAndAction(
                                        schedule.method,
                                        schedule.action
                                    ) === ETransactionType.CREDIT
                                        ? 'arrow_up'
                                        : 'arrow_down'
                                }
                            />
                            {getTitle}
                        </Typography>
                    </div>
                    <Status variant={getScheduleStatusVariant(schedule?.status)}>
                        {schedule?.status &&
                            getBillingStatusList(frequencyLocale).find(
                                (item) => item.value === schedule?.status
                            )?.label}
                    </Status>
                    <div className={styles.details}>
                        <div className={styles.row}>
                            <div>
                                <b style={{ fontWeight: 600 }}>${schedule.amount.toFixed(2)} </b>{' '}
                                <span className={styles.light_gray}>
                                    /{' '}
                                    {getFrequencyList(frequencyLocale)
                                        .find((item) => item.value === schedule.frequency)
                                        ?.label?.toLowerCase() || ''}
                                </span>
                            </div>
                            <div>
                                Occurrences:{' '}
                                {[EScheduleStatus.ERROR, EScheduleStatus.COMPLETE].includes(
                                    schedule.status
                                )
                                    ? '0'
                                    : schedule.remaining_payments > 0
                                      ? schedule.total_payments + schedule.remaining_payments
                                      : 'Indefinite'}
                            </div>
                        </div>
                        <div className={styles.progress}>
                            <div className={styles.progress_title}>
                                {schedule.remaining_payments > 0
                                    ? `${isRequest ? 'Requests' : 'Payments'} remaining: ${schedule.remaining_payments}`
                                    : `${isRequest ? 'Requested' : 'Paid'}: $${calculateTotalPaid.toFixed(2)}`}{' '}
                                {schedule.remaining_payments <= 0 && (
                                    <span>({schedule.total_payments} payments)</span>
                                )}
                            </div>
                            {schedule.remaining_payments > 0 && (
                                <div className={styles.progress_bg}>
                                    <div
                                        style={{
                                            width: `${(100 / (schedule.total_payments + schedule.remaining_payments)) * schedule.total_payments}%`
                                        }}
                                    />
                                </div>
                            )}
                            {schedule.remaining_payments > 0 && (
                                <div className={styles.row}>
                                    <div>Paid: ${calculateTotalPaid.toFixed(2)}</div>
                                    <div>Remaining: ${calculateRemainingAmount.toFixed(2)}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className={styles.tabs_section}>
                    <Tabs
                        onChange={(val) => setActiveTab(val as TScheduleTab)}
                        tabs={tabList}
                        activeTab={activeTab}
                    />
                    {activeTab === 'schedule' && (
                        <div className={styles.dates}>
                            <div className={styles.row}>
                                <div className={styles.date}>
                                    <Icon name="calendar" />
                                    <div className={styles.text_wrapper}>
                                        <p>Start date</p>
                                        <span>
                                            {formatDate(
                                                getStartDate({
                                                    nextPaymentAt: schedule.next_payment_at,
                                                    lastPaymentAt: schedule.last_payment_at,
                                                    frequency: schedule.frequency,
                                                    donePayments: schedule.total_payments
                                                }),
                                                'MM/DD/YYYY'
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.dashed_divider} />
                                <div className={styles.date}>
                                    <Icon name="calendar_check" />
                                    <div className={styles.text_wrapper}>
                                        <p>End date</p>
                                        <span>
                                            {schedule?.remaining_payments >= 0
                                                ? formatDate(
                                                      getLastPaymentDate({
                                                          nextPaymentAt: schedule.next_payment_at,
                                                          lastPaymentAt: schedule.last_payment_at,
                                                          frequency: schedule.frequency,
                                                          number_of_payments:
                                                              schedule?.remaining_payments,
                                                          donePayments: schedule.total_payments
                                                      }),
                                                      'MM/DD/YYYY'
                                                  )
                                                : 'Until canceled'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.divider} />
                            <div className={styles.row}>
                                <div className={styles.date}>
                                    <Icon name="reload" />
                                    <div className={styles.text_wrapper}>
                                        <p>Payment frequency </p>
                                        <span>
                                            {getScheduleDescription(
                                                getStartDate({
                                                    nextPaymentAt: schedule.next_payment_at,
                                                    lastPaymentAt: schedule.last_payment_at,
                                                    frequency: schedule.frequency,
                                                    donePayments: schedule.total_payments
                                                }),
                                                schedule.frequency
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.date}>
                                    <div
                                        className={cx([
                                            styles.dot,
                                            getScheduleStatusVariant(schedule?.status)
                                        ])}
                                    />
                                    <div className={styles.text_wrapper}>
                                        <p>{getNextPaymentText}</p>
                                        <span>
                                            {formatDate(
                                                schedule?.status !== EScheduleStatus.ACTIVE
                                                    ? schedule.last_payment_at
                                                    : getNextPaymentDate({
                                                          frequency: schedule.frequency,
                                                          nextPaymentAt: schedule.next_payment_at,
                                                          lastPaymentAt: schedule.last_payment_at
                                                      }),
                                                'MM/DD/YYYY'
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {activeTab === 'details' && (
                        <div className={styles.tab_details}>
                            {(() => {
                                const currency = transactions?.data?.[0]?.currency ?? 'CAD';
                                const symbol = currencies[currency as 'CAD']?.symbol ?? '$';
                                const fee = pointsFeeData?.fee ?? 0;
                                const grossAmount = schedule.amount + fee;
                                return (
                                    <>
                                        {pointsFeeData && (
                                            <div className={styles.details_row}>
                                                <span>Amount you send</span>
                                                <span className={styles.value_bold}>
                                                    {symbol}
                                                    {prettifyAmount(grossAmount.toFixed(2))}{' '}
                                                    {currency}
                                                </span>
                                            </div>
                                        )}
                                        {pointsFeeData && (
                                            <div className={styles.details_row}>
                                                <span className={styles.label_with_info}>
                                                    Total fees (included)
                                                    <button
                                                        type="button"
                                                        className={styles.fee_info_btn}
                                                        onClick={() => setShowFees(true)}>
                                                        <Icon name="info" width={16} height={16} />
                                                    </button>
                                                </span>
                                                <span>
                                                    {symbol}
                                                    {prettifyAmount(fee.toFixed(2))} {currency}
                                                </span>
                                            </div>
                                        )}
                                        <div className={styles.details_row}>
                                            <span>{getRecipientName} gets</span>
                                            <span className={styles.value_bold}>
                                                {symbol}
                                                {prettifyAmount(schedule.amount.toFixed(2))}{' '}
                                                {currency}
                                            </span>
                                        </div>
                                        {(schedule.method === ETransactionMethod.WALLET ||
                                            schedule.method ===
                                                ETransactionMethod.E_TRANSFER) && (
                                            <div className={styles.details_row}>
                                                <span>Message</span>
                                                <span>-</span>
                                            </div>
                                        )}
                                        {pointsFeeData && (
                                            <div className={styles.details_row}>
                                                <span>Points per payment</span>
                                                <span>{pointsFeeData.points}</span>
                                            </div>
                                        )}
                                        <div className={styles.details_row}>
                                            <span>Schedule ID</span>
                                            <span
                                                className={styles.schedule_id_value}
                                                onClick={() =>
                                                    copyStringToClipboard(
                                                        schedule.reference_code ?? ''
                                                    )
                                                }>
                                                {schedule.reference_code ?? '—'}
                                            </span>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}
                    {activeTab === 'transactions' && !isRequest && (
                        <div className={styles.tab_transactions}>
                            {transactions?.data?.map((item) => {
                                return (
                                    <div
                                        className={styles.item}
                                        key={item.id}
                                        onClick={() => {
                                            setChosenTransaction(item.id);
                                        }}>
                                        <Typography variant="body3">
                                            {formatDate(item.created_at, 'DD MMM YYYY')}
                                        </Typography>
                                        <div className={styles.right}>
                                            {item.status !== ETransactionStatus.APPROVED && (
                                                <Status
                                                    variant={getTransactionStatus(item?.status)}
                                                    className={styles.status}>
                                                    {item?.status &&
                                                        //@ts-ignore
                                                        ETransactionStatusTranslate[item.status][
                                                            i18n.language as 'en' | 'fr'
                                                        ]}
                                                </Status>
                                            )}
                                            <Typography
                                                variant="body1"
                                                weight="bold"
                                                className={amountStyles(item)}>
                                                {item.type === ETransactionType.CREDIT && '-'}
                                                {currencies[item.currency as 'CAD']?.symbol}
                                                {prettifyAmount(transactionAmount(item).toFixed(2))}
                                            </Typography>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {activeTab === 'requests' && isRequest && (
                        <div className={styles.tab_panel}>
                            <Typography variant="body4" className={styles.empty_state}>
                                Request history is not available yet
                            </Typography>
                        </div>
                    )}
                </div>
            </div>
            {showActionBar && (
                <div className={styles.actions_row}>
                    <Button
                        variant="outlined-red"
                        size="medium"
                        onClick={() => handleCancelPayment(schedule.id)}>
                        {isRequest
                            ? submitLocale('cancel_request')
                            : submitLocale('cancel_payment')}
                    </Button>
                    {(schedule.status === EScheduleStatus.ACTIVE ||
                        schedule.status === EScheduleStatus.ERROR) && (
                        <Button
                            variant="outlined-primary"
                            size="medium"
                            onClick={() => handleDeactivatePayment(schedule.id)}>
                            {submitLocale('pause')}
                        </Button>
                    )}
                    <Button
                        variant="outlined-primary"
                        size="medium"
                        onClick={() => onEdit(schedule)}>
                        {submitLocale('edit')}
                    </Button>
                    {schedule.status === EScheduleStatus.INACTIVE && (
                        <Button
                            variant="outlined-primary"
                            size="medium"
                            onClick={() => handleActivatePayment(schedule.id)}>
                            {submitLocale('restart')}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default ViewScheduleModal;
